import {
  MAIL_NOTIFICATION_SOUND_OPTIONS,
  getMailNotificationSoundOption,
  mailNotificationSoundUrl,
  readMailNotificationSoundId,
  type MailNotificationSoundId,
} from "@/lib/mail/mailNotificationSoundPrefs";

const MIN_GAP_MS = 2_500;

const SOUND_FILE_IDS = MAIL_NOTIFICATION_SOUND_OPTIONS.filter((o) => o.file != null).map(
  (o) => o.id
);

/** Un élément Audio préchargé par son — évite de re-télécharger à chaque écoute. */
const audioById = new Map<MailNotificationSoundId, HTMLAudioElement>();
const readyIds = new Set<MailNotificationSoundId>();
const failedIds = new Set<MailNotificationSoundId>();
const readyWaiters = new Map<MailNotificationSoundId, Array<(ok: boolean) => void>>();
const progressListeners = new Set<() => void>();
const playbackListeners = new Set<() => void>();

let lastPlayedAt = 0;
let playGen = 0;
let preloadStarted = false;
let preloadPromise: Promise<void> | null = null;
let playingId: MailNotificationSoundId | null = null;

export type MailNotificationSoundPreloadProgress = {
  total: number;
  done: number;
  ready: number;
  failed: number;
  /** 0–100 */
  percent: number;
  complete: boolean;
};

function computeProgress(): MailNotificationSoundPreloadProgress {
  const total = SOUND_FILE_IDS.length;
  const ready = SOUND_FILE_IDS.filter((id) => readyIds.has(id)).length;
  const failed = SOUND_FILE_IDS.filter((id) => failedIds.has(id)).length;
  const done = ready + failed;
  return {
    total,
    done,
    ready,
    failed,
    percent: total === 0 ? 100 : Math.round((done / total) * 100),
    complete: total === 0 || done >= total,
  };
}

/** Snapshot stable pour useSyncExternalStore (évite les re-renders infinis). */
let cachedProgress: MailNotificationSoundPreloadProgress = computeProgress();

function emitProgress(): void {
  cachedProgress = computeProgress();
  progressListeners.forEach((cb) => cb());
}

function emitPlayback(): void {
  playbackListeners.forEach((cb) => cb());
}

function setPlayingId(id: MailNotificationSoundId | null): void {
  if (playingId === id) return;
  playingId = id;
  emitPlayback();
}

export function getMailNotificationSoundPlayingId(): MailNotificationSoundId | null {
  return playingId;
}

export function subscribeMailNotificationSoundPlayback(listener: () => void): () => void {
  playbackListeners.add(listener);
  return () => playbackListeners.delete(listener);
}

export function getMailNotificationSoundPreloadProgress(): MailNotificationSoundPreloadProgress {
  return cachedProgress;
}

export function subscribeMailNotificationSoundPreload(listener: () => void): () => void {
  progressListeners.add(listener);
  return () => progressListeners.delete(listener);
}

function resolveUrl(soundId?: MailNotificationSoundId): string | null {
  const option = getMailNotificationSoundOption(soundId);
  if (!option.file) return null;
  return mailNotificationSoundUrl(option.file);
}

function notifyReady(id: MailNotificationSoundId, ok: boolean): void {
  if (ok) {
    readyIds.add(id);
    failedIds.delete(id);
  } else {
    failedIds.add(id);
  }
  emitProgress();

  const waiters = readyWaiters.get(id);
  if (!waiters) return;
  readyWaiters.delete(id);
  for (const resolve of waiters) resolve(ok);
}

function waitUntilReady(id: MailNotificationSoundId, el: HTMLAudioElement): Promise<boolean> {
  if (readyIds.has(id)) return Promise.resolve(true);
  if (failedIds.has(id) || el.error) return Promise.resolve(false);
  if (el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    readyIds.add(id);
    emitProgress();
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const list = readyWaiters.get(id) ?? [];
    list.push(resolve);
    readyWaiters.set(id, list);

    const onReady = () => {
      el.removeEventListener("canplaythrough", onReady);
      el.removeEventListener("loadeddata", onReady);
      el.removeEventListener("error", onError);
      notifyReady(id, true);
    };
    const onError = () => {
      el.removeEventListener("canplaythrough", onReady);
      el.removeEventListener("loadeddata", onReady);
      el.removeEventListener("error", onError);
      notifyReady(id, false);
    };

    el.addEventListener("canplaythrough", onReady);
    el.addEventListener("loadeddata", onReady);
    el.addEventListener("error", onError);
  });
}

function ensureAudio(id: MailNotificationSoundId): HTMLAudioElement | null {
  const url = resolveUrl(id);
  if (!url) return null;

  let el = audioById.get(id);
  if (el) return el;

  try {
    el = new Audio();
    el.preload = "auto";
    el.src = url;
    audioById.set(id, el);

    const markReady = () => notifyReady(id, true);
    const markError = () => notifyReady(id, false);
    el.addEventListener("canplaythrough", markReady, { once: true });
    el.addEventListener("loadeddata", markReady, { once: true });
    el.addEventListener("error", markError, { once: true });
    el.load();
    return el;
  } catch {
    notifyReady(id, false);
    return null;
  }
}

/** Précharge tous les sons (appelé à l’ouverture du panneau / unlock). */
export function preloadMailNotificationSounds(): Promise<void> {
  if (preloadPromise) return preloadPromise;

  preloadStarted = true;
  emitProgress();
  preloadPromise = (async () => {
    const tasks: Promise<unknown>[] = [];
    for (const id of SOUND_FILE_IDS) {
      const el = ensureAudio(id);
      if (!el) {
        notifyReady(id, false);
        continue;
      }
      tasks.push(waitUntilReady(id, el));
    }
    await Promise.allSettled(tasks);
    emitProgress();
  })();

  return preloadPromise;
}

export function isMailNotificationSoundReady(id: MailNotificationSoundId): boolean {
  return id === "off" || readyIds.has(id);
}

async function playId(id: MailNotificationSoundId, respectGap: boolean): Promise<void> {
  if (id === "off") return;

  const now = Date.now();
  if (respectGap && now - lastPlayedAt < MIN_GAP_MS) return;
  lastPlayedAt = now;

  if (!preloadStarted) {
    void preloadMailNotificationSounds();
  }

  const el = ensureAudio(id);
  if (!el) throw new Error("son indisponible");

  const ready = await waitUntilReady(id, el);
  if (!ready) throw new Error("chargement audio impossible");

  const gen = ++playGen;

  for (const [otherId, other] of audioById) {
    if (otherId === id) continue;
    if (!other.paused) {
      other.pause();
      other.currentTime = 0;
    }
  }

  el.pause();
  try {
    el.currentTime = 0;
  } catch {
    /* ignore */
  }

  const clearIfCurrent = () => {
    if (gen === playGen) setPlayingId(null);
  };

  el.addEventListener("ended", clearIfCurrent, { once: true });

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (gen !== playGen || settled) return;
      settled = true;
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("error", onError);
      if (ok) {
        setPlayingId(id);
        resolve();
      } else {
        setPlayingId(null);
        reject(new Error("lecture audio impossible"));
      }
    };
    const onPlaying = () => finish(true);
    const onError = () => finish(false);

    el.addEventListener("playing", onPlaying);
    el.addEventListener("error", onError);

    void el.play().then(
      () => {
        if (!el.paused) finish(true);
      },
      () => finish(false)
    );
  });
}

/** Stoppe l’aperçu en cours (bouton pause). */
export function pauseMailNotificationSoundPreview(): void {
  playGen += 1;
  const current = playingId;
  if (current) {
    const el = audioById.get(current);
    if (el) {
      el.pause();
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  }
  setPlayingId(null);
}

/**
 * Play / pause pour l’aperçu : si ce son joue déjà → pause, sinon lecture.
 * @returns `"playing"` | `"paused"`
 */
export async function toggleMailNotificationSoundPreview(
  soundId: MailNotificationSoundId
): Promise<"playing" | "paused"> {
  if (soundId === "off") return "paused";
  if (playingId === soundId) {
    pauseMailNotificationSoundPreview();
    return "paused";
  }
  await playId(soundId, false);
  return "playing";
}

/** Son de notification — respecte la préférence (ou silencieux). */
export function playMailNotificationSound(): void {
  const id = readMailNotificationSoundId();
  if (id === "off") return;
  void playId(id, true);
}

/** Aperçu depuis le menu (ignore l’anti-spam). */
export function previewMailNotificationSound(soundId: MailNotificationSoundId): Promise<void> {
  return playId(soundId, false);
}

/**
 * Débloque l’audio après un geste utilisateur et précharge le son choisi
 * (+ les autres en arrière-plan pour les aperçus instantanés).
 */
export function unlockMailNotificationSound(): void {
  const selected = readMailNotificationSoundId();
  if (selected !== "off") {
    const el = ensureAudio(selected);
    if (el) {
      el.muted = true;
      void el
        .play()
        .then(() => {
          el.pause();
          el.currentTime = 0;
          el.muted = false;
          readyIds.add(selected);
          emitProgress();
        })
        .catch(() => {
          el.muted = false;
        });
    }
  }
  void preloadMailNotificationSounds();
}
